import { useMemo, useState } from 'react'
import { Button, Checkbox, Input } from 'antd'
import { BulbOutlined, CloseCircleFilled, EditOutlined, PlusOutlined } from '@ant-design/icons'
import { useToast } from '@/contexts/ToastContext'
import { useAssistant } from '@/features/advisory-ai/assistant'
import { useBatchUpdateFindingsMutation } from '@/features/advisory-ai/hooks'
import {
  AdvisoryFindingType,
  type AdvisoryAgendaItemSummary,
  type AdvisoryFinding,
  type AdvisoryFindingTypeValue,
} from '@/features/advisory-ai/types'
import {
  FindingsChangesConfirmModal,
  type FindingDiff,
} from './FindingsChangesConfirmModal'

const { TextArea } = Input

type ColumnKey = AdvisoryFindingTypeValue

type ReportAgendaFindingsSectionProps = {
  callId: string
  clientId: string
  agendaItems: AdvisoryAgendaItemSummary[]
  findings: AdvisoryFinding[]
}

function emptyColumns(): Record<ColumnKey, AdvisoryFinding[]> {
  return {
    [AdvisoryFindingType.METRICS]: [],
    [AdvisoryFindingType.FINDINGS]: [],
    [AdvisoryFindingType.RECOMMENDATIONS]: [],
  }
}

function isNewId(id: string) {
  return id.startsWith('new-')
}

function nextNewId() {
  return `new-${Math.random().toString(36).slice(2, 9)}`
}

function computeDiff(
  original: AdvisoryFinding[],
  working: AdvisoryFinding[],
  agendaId: string
): FindingDiff {
  const origInAgenda = original.filter((f) => f.advisoryAgendaId === agendaId)
  const workInAgenda = working.filter((f) => f.advisoryAgendaId === agendaId)
  const origById = new Map(origInAgenda.map((f) => [f.advisoryFindingId, f]))

  const created: FindingDiff['created'] = []
  const updated: FindingDiff['updated'] = []

  workInAgenda.forEach((w) => {
    if (isNewId(w.advisoryFindingId) || !origById.has(w.advisoryFindingId)) {
      created.push({ type: w.type, heading: w.heading ?? null, description: w.description, hidden: w.hidden })
      return
    }
    const orig = origById.get(w.advisoryFindingId)!
    const headingChanged = (orig.heading || '') !== (w.heading || '')
    const descriptionChanged = orig.description !== w.description
    const typeChanged = String(orig.type).toLowerCase() !== String(w.type).toLowerCase()
    const hiddenChanged = Boolean(orig.hidden) !== Boolean(w.hidden)
    if (headingChanged || descriptionChanged || typeChanged || hiddenChanged) {
      updated.push({ before: orig, after: w })
    }
  })

  const workIds = new Set(workInAgenda.map((f) => f.advisoryFindingId))
  const deleted = origInAgenda.filter((f) => !workIds.has(f.advisoryFindingId))

  return { created, updated, deleted }
}

export function ReportAgendaFindingsSection({
  callId,
  clientId,
  agendaItems,
  findings,
}: ReportAgendaFindingsSectionProps) {
  const { addToast } = useToast()
  const { notifyFindingsSaved } = useAssistant().actions
  const batchMutation = useBatchUpdateFindingsMutation(callId, clientId)

  const [editingAgendaId, setEditingAgendaId] = useState<string | null>(null)
  const [working, setWorking] = useState<AdvisoryFinding[]>([])
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [pendingDiff, setPendingDiff] = useState<FindingDiff | null>(null)

  const groupedDisplay = useMemo(() => {
    const out: Record<string, Record<ColumnKey, AdvisoryFinding[]>> = {}
    findings.forEach((f) => {
      const aId = f.advisoryAgendaId || 'unassigned'
      if (!out[aId]) out[aId] = emptyColumns()
      const t = String(f.type || '').toLowerCase() as ColumnKey
      if (t in out[aId]) out[aId][t].push(f)
    })
    return out
  }, [findings])

  const groupedWorking = useMemo(() => {
    const out: Record<string, Record<ColumnKey, AdvisoryFinding[]>> = {}
    working.forEach((f) => {
      const aId = f.advisoryAgendaId || 'unassigned'
      if (!out[aId]) out[aId] = emptyColumns()
      const t = String(f.type || '').toLowerCase() as ColumnKey
      if (t in out[aId]) out[aId][t].push(f)
    })
    return out
  }, [working])

  const startEdit = (agendaId: string) => {
    setEditingAgendaId(agendaId)
    setWorking(findings.map((f) => ({ ...f })))
  }

  const cancelEdit = () => {
    setEditingAgendaId(null)
    setWorking([])
    setConfirmOpen(false)
    setPendingDiff(null)
  }

  const updateRow = (id: string, patch: Partial<AdvisoryFinding>) => {
    setWorking((prev) => prev.map((f) => (f.advisoryFindingId === id ? { ...f, ...patch } : f)))
  }

  const removeRow = (id: string) => {
    setWorking((prev) => prev.filter((f) => f.advisoryFindingId !== id))
  }

  const addRow = (agendaId: string, type: ColumnKey) => {
    setWorking((prev) => [
      ...prev,
      {
        advisoryFindingId: nextNewId(),
        advisoryCallId: callId,
        advisoryAgendaId: agendaId,
        type,
        heading: type === AdvisoryFindingType.FINDINGS ? null : '',
        description: '',
        createdAt: new Date().toISOString(),
        hidden: false,
      },
    ])
  }

  const requestSave = () => {
    if (!editingAgendaId) return
    const workInAgenda = working.filter((f) => f.advisoryAgendaId === editingAgendaId)
    const hasBlankDescription = workInAgenda.some((f) => !f.description.trim())
    if (hasBlankDescription) {
      addToast('Every item needs a description before saving', 'error')
      return
    }
    const diff = computeDiff(findings, working, editingAgendaId)
    if (diff.created.length === 0 && diff.updated.length === 0 && diff.deleted.length === 0) {
      addToast('No changes to save', 'info')
      cancelEdit()
      return
    }
    setPendingDiff(diff)
    setConfirmOpen(true)
  }

  const confirmSave = async () => {
    if (!editingAgendaId || !pendingDiff) return
    const agendaId = editingAgendaId
    const diff = pendingDiff
    // Leave edit mode immediately. The mutation applies the change to the cached report
    // optimistically (and rolls back on failure), so it shows right away without waiting for
    // the round-trip; the settled refetch reconciles with the server.
    setConfirmOpen(false)
    setPendingDiff(null)
    setEditingAgendaId(null)
    setWorking([])
    try {
      await batchMutation.mutateAsync({
        advisoryAgendaId: agendaId,
        created: diff.created.map((c) => ({
          type: c.type,
          heading: c.heading ?? null,
          description: c.description.trim(),
          hidden: c.hidden ?? false,
        })),
        updated: diff.updated.map(({ after }) => ({
          advisoryFindingId: after.advisoryFindingId,
          type: after.type,
          heading: after.heading ?? null,
          description: after.description.trim(),
          hidden: after.hidden ?? false,
        })),
        deleted: diff.deleted.map((d) => d.advisoryFindingId),
      })
      addToast('Changes saved', 'success')
      notifyFindingsSaved()
    } catch (error) {
      addToast(error instanceof Error ? error.message : 'Failed to save changes', 'error')
    }
  }

  const renderDisplayRow = (f: AdvisoryFinding, accent: 'metric' | 'finding' | 'rec') => {
    const hidden = Boolean(f.hidden)
    return (
      <div key={f.advisoryFindingId} className="flex items-start gap-3">
        {accent === 'rec' ? (
          <BulbOutlined className={`mt-1 flex-shrink-0 ${hidden ? 'text-gray-300' : 'text-yellow-500'}`} />
        ) : (
          <div
            className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
              hidden ? 'bg-gray-300' : accent === 'metric' ? 'bg-green-500' : 'bg-gray-500'
            }`}
          />
        )}
        <div className="flex-1">
          {f.heading ? (
            <p
              className={`${
                accent === 'rec' ? 'text-xs font-bold uppercase tracking-wide' : 'text-sm font-semibold'
              } ${hidden ? 'text-gray-400' : 'text-gray-900'}`}
            >
              {f.heading}
              {hidden ? (
                <span className="ml-2 text-[10px] font-medium uppercase tracking-wide text-gray-400">Hidden</span>
              ) : null}
            </p>
          ) : null}
          <p className={`text-sm whitespace-pre-wrap ${hidden ? 'text-gray-400' : 'text-gray-700'}`}>
            {f.description}
            {hidden && !f.heading ? (
              <span className="ml-2 text-[10px] font-medium uppercase tracking-wide text-gray-400">Hidden</span>
            ) : null}
          </p>
        </div>
      </div>
    )
  }

  const renderEditRow = (f: AdvisoryFinding, accent: 'metric' | 'finding' | 'rec') => (
    <div
      key={f.advisoryFindingId}
      className="relative rounded-md border border-gray-200 bg-gray-50/60 p-3 pr-8"
    >
      <button
        type="button"
        onClick={() => removeRow(f.advisoryFindingId)}
        className="absolute top-2 right-2 text-gray-400 hover:text-red-500"
        aria-label="Remove"
      >
        <CloseCircleFilled />
      </button>
      <div className="mb-2">
        <Checkbox
          checked={Boolean(f.hidden)}
          onChange={(e) => updateRow(f.advisoryFindingId, { hidden: e.target.checked })}
        >
          <span className="text-xs text-gray-600">Hide from outputs</span>
        </Checkbox>
      </div>
      {accent !== 'finding' ? (
        <Input
          size="small"
          placeholder="Heading"
          className="mb-2"
          value={f.heading || ''}
          onChange={(e) => updateRow(f.advisoryFindingId, { heading: e.target.value })}
        />
      ) : null}
      <TextArea
        autoSize={{ minRows: 2, maxRows: 6 }}
        placeholder="Description"
        value={f.description}
        onChange={(e) => updateRow(f.advisoryFindingId, { description: e.target.value })}
      />
    </div>
  )

  return (
    <div className="space-y-6">
      {agendaItems.map((agendaItem, index) => {
        const isEditing = editingAgendaId === agendaItem.advisoryAgendaId
        const cols = isEditing
          ? groupedWorking[agendaItem.advisoryAgendaId] || emptyColumns()
          : groupedDisplay[agendaItem.advisoryAgendaId] || emptyColumns()
        const editDisabled = editingAgendaId !== null && !isEditing
        return (
          <div
            key={agendaItem.advisoryAgendaId}
            data-section={agendaItem.title}
            data-tour={isEditing ? 'finding-editing' : undefined}
            className={`bg-white border rounded-lg p-6 shadow-sm transition-colors ${
              isEditing ? 'feature-editing-panel' : 'border-gray-200'
            }`}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">
                {agendaItems.length > 1 ? `${index + 1}. ${agendaItem.title}` : agendaItem.title}
              </h3>
              {!isEditing ? (
                <Button
                  data-tour="finding-edit"
                  icon={<EditOutlined />}
                  onClick={() => startEdit(agendaItem.advisoryAgendaId)}
                  disabled={editDisabled}
                >
                  Edit
                </Button>
              ) : (
                <span className="feature-editing-badge text-xs uppercase tracking-wide font-semibold">
                  Editing
                </span>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">
                  Key metrics
                </h4>
                <div className="space-y-2">
                  {cols.metrics.map((f) =>
                    isEditing ? renderEditRow(f, 'metric') : renderDisplayRow(f, 'metric')
                  )}
                  {!isEditing && cols.metrics.length === 0 && (
                    <p className="text-sm text-gray-400 italic">None</p>
                  )}
                </div>
                {isEditing ? (
                  <button
                    type="button"
                    onClick={() => addRow(agendaItem.advisoryAgendaId, AdvisoryFindingType.METRICS)}
                    className="feature-link mt-3 inline-flex items-center gap-1 text-xs"
                  >
                    <PlusOutlined /> Add metric
                  </button>
                ) : null}
              </div>
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Findings</h4>
                <div className="space-y-2">
                  {cols.findings.map((f) =>
                    isEditing ? renderEditRow(f, 'finding') : renderDisplayRow(f, 'finding')
                  )}
                  {!isEditing && cols.findings.length === 0 && (
                    <p className="text-sm text-gray-400 italic">None</p>
                  )}
                </div>
                {isEditing ? (
                  <button
                    type="button"
                    onClick={() => addRow(agendaItem.advisoryAgendaId, AdvisoryFindingType.FINDINGS)}
                    className="feature-link mt-3 inline-flex items-center gap-1 text-xs"
                  >
                    <PlusOutlined /> Add finding
                  </button>
                ) : null}
              </div>
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">
                  Recommendations
                </h4>
                <div className="space-y-2">
                  {cols.recommendations.map((f) =>
                    isEditing ? renderEditRow(f, 'rec') : renderDisplayRow(f, 'rec')
                  )}
                  {!isEditing && cols.recommendations.length === 0 && (
                    <p className="text-sm text-gray-400 italic">None</p>
                  )}
                </div>
                {isEditing ? (
                  <button
                    type="button"
                    onClick={() =>
                      addRow(agendaItem.advisoryAgendaId, AdvisoryFindingType.RECOMMENDATIONS)
                    }
                    className="feature-link mt-3 inline-flex items-center gap-1 text-xs"
                  >
                    <PlusOutlined /> Add recommendation
                  </button>
                ) : null}
              </div>
            </div>
            {isEditing ? (
              <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-gray-200">
                <Button onClick={cancelEdit} disabled={batchMutation.isPending}>
                  Cancel
                </Button>
                <Button
                  type="primary"
                  onClick={requestSave}
                  loading={batchMutation.isPending && confirmOpen}
                >
                  Save changes
                </Button>
              </div>
            ) : null}
          </div>
        )
      })}

      <FindingsChangesConfirmModal
        open={confirmOpen}
        agendaTitle={
          agendaItems.find((a) => a.advisoryAgendaId === editingAgendaId)?.title ?? ''
        }
        diff={pendingDiff ?? { created: [], updated: [], deleted: [] }}
        saving={batchMutation.isPending}
        onConfirm={() => void confirmSave()}
        onCancel={() => {
          if (batchMutation.isPending) return
          setConfirmOpen(false)
          setPendingDiff(null)
        }}
      />
    </div>
  )
}
